import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type Plan, type WorkspaceDetail } from '../../api';
import { Notice } from '../Panels';
import { Hint } from '../Hint';
import { useToast } from '../Toast';

/**
 * The plan the workspace runs on and how much of this month's allowance is
 * used. Plan switches apply immediately; billing itself is connected separately
 * by the deployment owner.
 */
export function Billing() {
  const toast = useToast();
  const [detail, setDetail] = useState<WorkspaceDetail | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api
      .workspace()
      .then(setDetail)
      .catch((error: unknown) => {
        if (error instanceof ApiFailure) setFailure(error);
      });
  }, []);

  useEffect(reload, [reload]);

  if (detail === null) {
    return (
      <>
        {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
        <p className="muted">
          <span className="spinner" /> Loading…
        </p>
      </>
    );
  }

  const { usage, limits, workspace } = detail;

  const bars: { label: string; used: number; allowed: number }[] = [
    { label: 'Datasets', used: usage.datasetsCreated, allowed: limits.datasetsPerMonth },
    { label: 'Rows', used: usage.rowsIngested, allowed: limits.rowsPerMonth },
    { label: 'AI summaries', used: usage.narrationsRun, allowed: limits.narrationsPerMonth },
  ];

  async function change(plan: Plan): Promise<void> {
    setBusy(true);
    try {
      await api.setPlan(plan);
      toast.success(`The workspace is now on the ${plan} plan.`);
      reload();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The plan could not be changed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="intro">
        <h2>
          Billing & plan
          <Hint text="Your plan sets how much the workspace can use each month: how many datasets, rows and people. The bars show where this month stands." />
        </h2>
        <p>
          {workspace.name} is on the <strong>{workspace.plan}</strong> plan.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <section className="card">
        <header>
          <h2>This month's usage</h2>
          <span className="sub">this calendar month · resets on the 1st</span>
        </header>
        <div className="usage-bars">
          {bars.map((bar) => {
            const fraction = bar.allowed === 0 ? 0 : Math.min(1, bar.used / bar.allowed);
            return (
              <div key={bar.label}>
                <div className="usage-label">
                  <span>{bar.label}</span>
                  <span>
                    {bar.used.toLocaleString('en-US')} / {bar.allowed.toLocaleString('en-US')}
                  </span>
                </div>
                <div className="meter">
                  {/* Amber past 80% so a limit is visible before it is hit. */}
                  <div
                    style={{
                      width: `${Math.round(fraction * 100)}%`,
                      background:
                        fraction >= 1 ? 'var(--critical)' : fraction > 0.8 ? 'var(--warning)' : 'var(--series-1)',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="card">
        <header>
          <h2>Choose a plan</h2>
          <span className="sub">{detail.members.length} of {limits.members} people used</span>
        </header>
        <div className="plan-grid">
          {(['free', 'pro', 'agency'] as const).map((plan) => (
            <div key={plan} className={plan === workspace.plan ? 'plan current' : 'plan'}>
              <div className="plan-name">{plan}</div>
              <ul>
                <li>{plan === 'free' ? '10' : plan === 'pro' ? '200' : '2,000'} datasets / month</li>
                <li>{plan === 'free' ? 'No' : plan === 'pro' ? '10' : '100'} scheduled refreshes</li>
                <li>{plan === 'agency' ? 'White-label exports' : 'DataVantage branding'}</li>
                <li>{plan === 'free' ? '1' : plan === 'pro' ? '5' : '50'} members</li>
              </ul>
              {plan === workspace.plan ? (
                <span className="badge good">current</span>
              ) : (
                <button className="ghost tiny" disabled={busy} onClick={() => void change(plan)}>
                  Switch to {plan}
                </button>
              )}
            </div>
          ))}
        </div>
        <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
          Switching applies immediately. Payment is collected by the team running this deployment —
          if a switch is refused, the message above says why.
        </p>
      </section>
    </>
  );
}
